export const spacingClasses = {
  mt4: "mt-4",
  mt5: "mt-5",
  mt6: "mt-6",
} as const;

export const actionStateClasses = {
  disabledButton: "disabled:cursor-not-allowed disabled:opacity-60",
} as const;

export const typographyToneClasses = {
  eyebrowWarm: "text-xs font-semibold uppercase tracking-[0.22em] text-[#a2572b]",
  eyebrowCool: "text-xs font-semibold uppercase tracking-[0.22em] text-[#34566a]",
  bodyMutedSm: "mt-2 text-sm text-[#5c5b57]",
  sectionTitleMd: "text-base font-semibold text-[#213547] sm:text-lg",
} as const;

export const centeredStatusPageClasses = {
  shellSpacing: "py-20",
  card: "text-center",
  primaryLink: "mt-6 inline-flex",
} as const;

export const aboutPageClasses = {
  heroGrid: "relative z-10 grid gap-6 px-5 py-5 sm:px-8 sm:py-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center",
  heroImage: "about-hero-image min-h-[340px] rounded-[28px] bg-cover bg-center shadow-[0_22px_60px_rgba(84,62,39,0.16)]",
  heroCard: "rounded-[28px] bg-[#fff4e7] p-6 sm:p-8",
  featureGrid: "mt-6 grid gap-3 sm:grid-cols-2",
  featureCard: "rounded-[22px] bg-white p-4 shadow-[0_12px_30px_rgba(84,62,39,0.08)]",
  featureCardAlt: "rounded-[22px] bg-[#e7f0ed] p-4 shadow-[0_12px_30px_rgba(84,62,39,0.08)]",
  eyebrowWarm: typographyToneClasses.eyebrowWarm,
  eyebrowCool: typographyToneClasses.eyebrowCool,
  bodySm: typographyToneClasses.bodyMutedSm,
  missionCard: spacingClasses.mt5,
  missionCopy: "text-base leading-8 text-[#5c5b57] sm:text-lg",
  strong: "font-semibold text-[#213547]",
  centerText: "text-center",
  offerGrid: "mt-6 grid grid-cols-1 gap-4 sm:mt-8 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3",
  offerCard: "p-5 sm:p-6",
  offerIcon: "mb-3",
  offerTitle: typographyToneClasses.sectionTitleMd,
  offerCopy: "mt-1 text-sm leading-6 text-[#5c5b57]",
  stepsGrid: "mt-6 grid grid-cols-1 gap-4 sm:mt-8 sm:grid-cols-3 sm:gap-6",
  stepCard: "text-center",
  stepTitle: `${spacingClasses.mt4} ${typographyToneClasses.sectionTitleMd}`,
  ctaSection: "text-center",
  ctaTitle: `${spacingClasses.mt4} text-lg font-semibold text-[#213547] sm:text-2xl`,
  ctaCopy: "mt-2 text-[#5c5b57]",
  ctaActions: "mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row",
  ctaLink: "w-full sm:w-auto",
} as const;

export const authPageClasses = {
  authForm: "space-y-5",
  authCardSpacing: spacingClasses.mt6,
  legalCopy: "mt-6 text-center text-xs text-slate-500",
  legalLink: "hover:underline",
  disabledButton: actionStateClasses.disabledButton,
} as const;

export const checkEmailPageClasses = {
  shellSpacing: centeredStatusPageClasses.shellSpacing,
  heading: spacingClasses.mt4,
  card: centeredStatusPageClasses.card,
  secondaryCopy: "mt-3 text-sm text-slate-600",
  primaryLink: centeredStatusPageClasses.primaryLink,
} as const;

export const contactPageClasses = {
  section: "mx-auto max-w-2xl",
  introCopy: "mt-1 text-sm text-slate-600",
  successBanner: "mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800",
  form: "mt-6 space-y-5",
  counterRow: "mt-1 flex items-center justify-between text-xs text-slate-500",
  submitWrap: "pt-2",
  footer: "mt-6 border-t border-slate-200/70 pt-4 text-sm text-slate-600",
  emailLink: "font-medium text-sky-700 hover:underline",
} as const;

export const createJobPageClasses = {
  loading: "flex min-h-[60vh] items-center justify-center text-slate-600",
  heading: spacingClasses.mt4,
  form: "space-y-6",
  dateInput: "appearance-none",
  actions: "flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end",
} as const;

export const homePageClasses = {
  pageSpacing: "space-y-0",
  heroSection: "mb-6 sm:mb-8",
  heroGrid: "relative z-10 grid gap-6 px-5 py-5 sm:px-8 sm:py-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-stretch",
  heroCopyCard: "flex flex-col justify-between rounded-[28px] bg-[#f7ecdc] p-6 sm:p-8",
  statsGrid: "mt-6 grid gap-3 sm:grid-cols-2",
  statCard: "rounded-[24px] bg-white p-5 shadow-[0_14px_35px_rgba(84,62,39,0.08)]",
  statCardAlt: "rounded-[24px] bg-[#e2efeb] p-5 shadow-[0_14px_35px_rgba(84,62,39,0.08)]",
  statLabelMuted: "text-sm text-[#7b766e]",
  statValue: "mt-2 text-3xl font-bold text-[#213547]",
  statLabel: "text-sm text-[#5c5b57]",
  statStatus: "mt-2 text-lg font-semibold text-[#213547]",
  mobileCtaWrap: "mt-6 lg:hidden",
  mobileCta: "inline-flex w-full sm:w-auto",
  desktopPanel: "hidden gap-4 lg:grid",
  heroImage: "home-hero-image min-h-[320px] rounded-[28px] bg-cover bg-center shadow-[0_22px_60px_rgba(84,62,39,0.16)]",
  featureGrid: "grid gap-4 sm:grid-cols-2",
  featureCard: "rounded-[24px] border border-[#d7ccb9] bg-[#fff8ef] p-5",
  featureCardAlt: "rounded-[24px] border border-[#c9ddd8] bg-[#edf4f2] p-5",
  featureEyebrowWarm: typographyToneClasses.eyebrowWarm,
  featureEyebrowCool: typographyToneClasses.eyebrowCool,
  featureCopy: "mt-2 text-base font-semibold text-[#213547]",
  jobsStack: "space-y-6 sm:space-y-8",
  jobsAnchor: "scroll-mt-24 sm:scroll-mt-28",
  postsSection: "space-y-4",
  refreshWrap: "flex min-w-[6.5rem] justify-end",
  refreshBadge: "inline-flex items-center gap-2 rounded-full bg-[#f7ecdc] px-3 py-2 text-xs font-medium text-[#7b766e]",
  refreshDot: "h-2 w-2 animate-pulse rounded-full bg-[#c96d3b]",
  sectionHeader: "flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between",
  sectionTitle: "text-2xl font-semibold text-[#213547]",
  sectionCopy: "text-sm text-[#5c5b57]",
  pageCount: "text-sm font-medium text-[#a2572b]",
  error: "mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700",
  cardGrid: "items-stretch auto-rows-fr",
  paginationWrap: "mt-8 min-h-[3rem]",
  pagination: "flex items-center justify-center gap-3 text-sm",
  disabledButton: actionStateClasses.disabledButton,
} as const;

export const jobDetailsPageClasses = {
  shellSpacing: "space-y-6 pb-24 sm:pb-16",
  loading: "flex min-h-[60vh] items-center justify-center text-[#5c5b57]",
  errorWrap: "flex min-h-[60vh] flex-col items-center justify-center gap-4 text-[#5c5b57]",
  errorCopy: "font-medium text-red-600",
  backLink: "text-sm text-[#a2572b] hover:underline",
  backButton: "inline-flex items-center gap-2 rounded-xl text-sm text-[#a2572b] hover:underline",
  titleBlock: "mt-5",
  title: "text-2xl font-bold tracking-tight text-[#213547] sm:text-4xl",
  company: "mt-2 text-base font-medium text-[#34566a]",
  metaGrid: "mt-5 grid grid-cols-1 gap-3 text-sm text-[#34566a] sm:grid-cols-2",
  metaRow: "flex items-center gap-2",
  metaIcon: "h-5 w-5 text-[#7b766e]",
  actions: "mt-6 hidden flex-row gap-3 sm:flex",
  savedButtonActive: "border-emerald-200 bg-emerald-50 text-emerald-800",
  savedIconActive: "text-emerald-600",
  savedIconIdle: "text-[#7b766e]",
  alerts: "mt-4",
  descriptionTitle: "text-lg font-semibold text-[#213547] sm:text-xl",
  descriptionCopy: "whitespace-pre-line",
  mobileBar:
    "fixed inset-x-0 bottom-0 z-40 border-t border-[#ddd1bf] bg-[#fffaf3]/95 p-3 backdrop-blur sm:hidden [padding-bottom:calc(env(safe-area-inset-bottom)+0.75rem)]",
  mobileBarInner: "mx-auto flex max-w-5xl items-center gap-3",
  mobilePrimary: "flex-1",
  disabledButton: actionStateClasses.disabledButton,
} as const;

export const loginPageClasses = {
  shellWidth: "w-full max-w-lg",
  heading: spacingClasses.mt4,
  error: "mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700",
  utilityRow: "mt-4 flex flex-col items-center justify-between gap-2 text-sm text-slate-600 sm:flex-row",
  utilityButton: "rounded-xl text-sm text-sky-700 hover:underline",
  utilityLink: "font-medium text-sky-700 hover:underline",
} as const;

export const registerPageClasses = {
  shellWidth: "w-full max-w-2xl",
  heading: spacingClasses.mt4,
  title: "text-center text-xl font-semibold text-slate-900",
  form: "mt-6 space-y-5",
  serverError: "text-center text-sm text-red-500",
  roleLabel: "mb-2 block text-sm font-medium text-slate-800",
  roleGrid: "flex flex-col gap-2 sm:flex-row",
  roleCardBase: "flex-1 cursor-pointer rounded-[24px] border px-4 py-4 text-left transition",
  roleCardSelected: "border-sky-400 bg-sky-50/80 shadow-[0_16px_35px_rgba(14,165,233,0.14)]",
  roleCardUnselected:
    "border-slate-200/80 bg-white/80 hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white",
  roleTitle: "text-sm font-semibold text-slate-900",
  roleCopy: "mt-1 text-xs text-slate-600",
  passwordHint: "mt-1 text-xs text-slate-500",
  loginPrompt: "mt-6 text-center text-sm text-slate-600",
  loginLink: "font-medium text-sky-700 hover:underline",
} as const;

export const userProfilePageClasses = {
  shellSpacing:
    "pt-6 pb-[5.5rem] space-y-6 sm:pt-8 sm:pb-14 sm:space-y-8 [padding-bottom:calc(env(safe-area-inset-bottom)+5.5rem)]",
  header: "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6",
  title: "text-2xl font-bold tracking-tight text-gray-900 sm:text-4xl",
  intro: "mt-1 text-sm text-gray-600 sm:text-base",
  primaryAction:
    "inline-flex w-full items-center justify-center gap-2 shadow-lg shadow-blue-600/20 sm:w-auto",
  primaryActionIcon: "h-5 w-5",
  tabsOuter: "-mx-3 px-3 sm:mx-0 sm:px-0",
  tabsScroll: "overflow-x-auto no-scrollbar",
  tabsList: "inline-flex min-w-max rounded-2xl bg-gray-100 p-1 shadow-inner",
  error: "rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700",
  sectionTitle: "mb-3 text-lg sm:mb-4 sm:text-2xl",
  grid: "grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3",
  secondaryActionPosted: "inline-flex w-full items-center justify-center gap-2 sm:w-auto",
  secondaryActionSaved: "w-full sm:w-auto",
  secondaryActionIcon: "h-4 w-4",
  tabButton:
    "relative whitespace-nowrap rounded-xl px-3 py-2 text-sm transition sm:px-4 sm:text-base",
  tabButtonActive: "bg-white text-gray-900 shadow",
  tabButtonInactive: "text-gray-600 hover:text-gray-900",
  tabIndicator: "absolute inset-x-3 -bottom-1 h-0.5 rounded bg-blue-600",
} as const;

export const verifyEmailPageClasses = {
  shellSpacing: centeredStatusPageClasses.shellSpacing,
  heading: spacingClasses.mt4,
  card: centeredStatusPageClasses.card,
  successIcon: "mx-auto h-10 w-10 text-emerald-600",
  loadingCopy: "text-slate-600",
  primaryLink: centeredStatusPageClasses.primaryLink,
} as const;

export const blogPageClasses = {
  heroSide: "hidden gap-4 lg:grid",
  heroImage: "blog-hero-image min-h-[320px] rounded-[28px] bg-cover bg-center shadow-[0_22px_60px_rgba(84,62,39,0.16)]",
  heroNoteGrid: "grid gap-4 sm:grid-cols-2",
  heroNoteWarm: "rounded-[24px] border border-[#d7ccb9] bg-[#fff8ef] p-5",
  heroNoteCool: "rounded-[24px] border border-[#c9ddd8] bg-[#edf4f2] p-5",
  heroNoteTitle: "text-sm font-semibold uppercase tracking-[0.18em] text-[#a2572b]",
  heroNoteCopy: "mt-2 text-base font-semibold text-[#213547]",
  sectionSpacing: "space-y-6",
  filterBar: "mb-6",
  filterGrid: "grid gap-3 sm:grid-cols-[1fr_auto]",
  searchInput: "w-full rounded-2xl border border-[#d9ccb8] bg-white px-4 py-3 text-sm text-[#213547] outline-none transition focus:border-[#c96d3b]",
  sortSelect: "rounded-2xl border border-[#d9ccb8] bg-[#fffaf3] px-4 py-3 text-sm font-medium text-[#34566a] outline-none",
  cardGrid: "grid gap-6 md:grid-cols-2 xl:grid-cols-3",
  card: "overflow-hidden rounded-[28px] border border-[#e0d3c0] bg-white shadow-[0_18px_45px_rgba(84,62,39,0.1)]",
  cardImage: "h-56 w-full object-cover",
  cardImageFallback: "h-56 w-full bg-[linear-gradient(135deg,#f7ecdc_0%,#e7f0ed_100%)]",
  cardBody: "space-y-4 p-5",
  cardMeta: "flex items-center justify-between gap-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#a2572b]",
  cardTitle: "text-2xl font-semibold tracking-tight text-[#213547]",
  cardSummary: "text-sm leading-7 text-[#5c5b57]",
  cardFooter: "flex items-center justify-between gap-3",
  cardReadTime: "text-sm text-[#5c5b57]",
  pagination: "flex items-center justify-center gap-3 pt-2",
  paginationText: "text-sm text-[#5c5b57]",
} as const;

export const blogPostPageClasses = {
  shellSpacing: "space-y-6 pb-14",
  headerWrap: "space-y-4",
  title: "mt-4 text-3xl font-bold tracking-tight text-[#213547] sm:text-5xl",
  summary: "max-w-3xl",
  meta: "flex flex-wrap items-center gap-3 text-sm font-medium text-[#5c5b57]",
  coverImage: "mt-6 h-[20rem] w-full rounded-[28px] object-cover sm:h-[28rem]",
  content: "space-y-5 text-base leading-8 text-[#34424c] [&_a]:text-[#a2572b] [&_a]:underline [&_blockquote]:rounded-[20px] [&_blockquote]:border-l-4 [&_blockquote]:border-[#c96d3b] [&_blockquote]:bg-[#fff8ef] [&_blockquote]:px-5 [&_blockquote]:py-4 [&_h2]:mt-8 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-[#213547] [&_h3]:mt-6 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-[#213547] [&_li]:ml-5 [&_li]:list-disc [&_p]:text-[#34424c]",
  galleryTitle: "text-xl font-semibold text-[#213547] sm:text-2xl",
  galleryGrid: "mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3",
  galleryItem: "overflow-hidden rounded-[24px] border border-[#e0d3c0] bg-[#fffaf3]",
  galleryImage: "h-56 w-full object-cover",
  galleryCaption: "p-4 text-sm text-[#5c5b57]",
} as const;
